import { getConfig } from '@shared/utils';
import { getLocale } from 'next-intl/server';
import type { ComponentProps } from 'react';
import type { MagazineArticle } from '../helpers/magazineCategory';
import { getFeaturedArticles } from './api/getFeaturedArticles';
import { FeaturedArticlesContent } from './components/FeaturedArticlesContent';

type FeaturedArticlesProps = Omit<ComponentProps<'div'>, 'children'> & {
  limit?: number;
  'data-testid'?: string;
};

type FeaturedArticlesSkeletonProps = {
  limit?: number;
  'data-testid'?: string;
};

function FeaturedArticlesSkeleton({ limit = 5, 'data-testid': dataTestId = 'featured-articles' }: FeaturedArticlesSkeletonProps) {
  return (
    <div className="flex w-full flex-col gap-6 pt-4" data-testid={`${dataTestId}-skeleton`}>
      <div className="flex items-center" role="tablist" aria-hidden="true" data-testid={`${dataTestId}-tabs-skeleton`}>
        <div className="h-6 w-28 rounded bg-muted" />
        <span className="mx-4 h-4 w-px bg-border" aria-hidden="true" />
        <div className="h-6 w-24 rounded bg-muted" />
      </div>
      <div className="flex w-full flex-col border-y border-border">
        {Array.from({ length: limit }).map((_, index) => (
          <div key={index} className="border-b border-dashed border-border px-2 py-6 last:border-b-0" data-testid={`${dataTestId}-loading-item`}>
            <div className="flex items-center gap-4">
              <div className="size-6 rounded-full bg-muted" />
              <div className="h-3 w-32 rounded bg-muted" />
            </div>
            <div className="ml-10 mt-2 h-4 w-52 rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}

async function FeaturedArticles({ limit = 5, ...props }: FeaturedArticlesProps) {
  const { apiUrl } = getConfig();
  const locale = await getLocale();
  let latestArticles: MagazineArticle[] = [];
  let recommendedArticles: MagazineArticle[] = [];
  let hasError = false;

  try {
    const featuredArticles = await getFeaturedArticles(apiUrl, { locale, limit });
    latestArticles = featuredArticles.latest;
    recommendedArticles = featuredArticles.recommended;
  } catch {
    hasError = true;
  }

  return <FeaturedArticlesContent latestArticles={latestArticles} recommendedArticles={recommendedArticles} hasError={hasError} {...props} />;
}

export { FeaturedArticles, FeaturedArticlesSkeleton, type FeaturedArticlesProps };
